import React, { useState, useCallback, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { FileText } from "lucide-react";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import SectionHeader from "../../components/ui/SectionHeader";
import FormField from "../../components/ui/FormField";
import RichTextEditor from "../../components/ui/RichTextEditor";
import { AboutSectionFieldLimits } from "../../config/fieldlimit";
import { useAuth } from "../../auth/AuthContext";
import {
  getWebsitePage,
  createOrUpdateWebsitePage,
} from "../../services/websitesetting";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";

const MAX_DESC = AboutSectionFieldLimits.description.max;

const PrivacyPolicyPage: React.FC = () => {
  const { user } = useAuth();
  const { subdomain } = useOutletContext<{ subdomain: string }>();
  const [formData, setFormData] = useState({
    heading: "",
    description: "",
  });
  const [pageId, setPageId] = useState<number | null>(null);
  const toast = useToast();
  const [errors, setErrors] = useState<{ heading?: string; description?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const charCount = formData.description?.length ?? 0;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const page = await getWebsitePage("privacy_policy");
        if (page) {
          const { title, description, id } = page;
          setFormData({
            heading: title || "",
            description: description || "",
          });
          setPageId(id);
        }
      } catch (error: any) {
        console.error("PrivacyPolicyPage fetchData ERROR:", error);
        const status = error.response?.status;
        if (status === 404) {
          toast.error("Privacy & Policy page not found. You can create it now.");
        } else {
          toast.error("Failed to load Privacy & Policy page settings.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!formData.heading.trim()) {
      newErrors.heading = "Heading is required.";
    } else if (formData.heading.length > AboutSectionFieldLimits.heading.max) {
      newErrors.heading = `Heading cannot exceed ${AboutSectionFieldLimits.heading.max} characters.`;
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: "heading" | "description", value: string) => {
    setFormData({ ...formData, [field]: value });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      toast.error("Please fix the validation errors.");
      return;
    }
    setIsSubmitting(true);

    try {
      const companyId =
        user?.company?.id ?? user?.company_id ?? user?.company?.company_id;
      await createOrUpdateWebsitePage({
        id: pageId ?? undefined,
        company_id: companyId,
        title: formData.heading,
        description: formData.description,
        key: "privacy_policy",
      });
      toast.success("Privacy & Policy page updated successfully!");
    } catch (error: any) {
      console.error("PrivacyPolicyPage handleSubmit ERROR:", error);

      if (error?.type === "serverValidation") {
        setErrors(error.errors);
        toast.error("Please fix the validation errors.");
      } else {
        const status = error.response?.status;
        let userMessage = "Failed to update Privacy & Policy page.";

        if (status === 404) {
          userMessage =
            "Privacy & Policy page data not found. Please refresh and try again.";
        } else if (status === 403) {
          userMessage = "You don't have permission to modify this page.";
        } else if (status === 422) {
          userMessage = "Invalid data provided. Please check all fields.";
        } else if (status === 500) {
          userMessage = "Internal server error. Please try again soon.";
        } else if (error.message === "Network Error") {
          userMessage = "Network problem detected. Check your connection.";
        }

        toast.error(userMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  return (
    <>
      {(isLoading || isSubmitting) && (
        <GlobalLoader
          initialPageLoad={isLoading}
          fullScreen={isSubmitting}
          description={
            isSubmitting ? "Saving changes..." : "Loading Privacy & Policy page..."
          }
        />
      )}
      <div className="space-y-5">
        <PageHeader
          title="Privacy & Policy Page"
          description="Manage the privacy & policy content shown on your website"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={<ViewWebsiteButton subdomain={subdomain} />}
        />

        <WebsiteSectionCard title="Page Content" icon={FileText}>
          <div className="space-y-6">
            <div>
              <SectionHeader title="Main Content" />

              <InfoContainer
                message="This content will appear on your Privacy & Policy page"
                iconSize={13}
                className="mb-4"
              />

              <div className="space-y-5">
                <FormField
                  label="Page Heading *"
                  name="heading"
                  value={formData.heading}
                  onChange={(_, val) => handleChange("heading", val)}
                  type="text"
                  placeholder="e.g. Privacy & Policy"
                  error={errors.heading}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(val) => handleChange("description", val)}
                    placeholder="Describe your privacy and policy details..."
                    className={errors.description ? "border-red-500" : ""}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-xs mt-1">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </WebsiteSectionCard>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;

